import React, { FC, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { Translate } from "@/app/application/Translate";
import type { TrainingDrillSessionSummaryWithKpiValue } from "@/features/training/model/Training/TrainingDrillSession";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import {
	Box,
	Chip,
	IconButton,
	LinearProgress,
	Paper,
	Skeleton,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import * as echarts from "echarts";
import ReactECharts from "echarts-for-react";

import {
	useFetchDrillSessionsByKpiId,
	useFetchTrainingKpi,
} from "@/service/TrainingQueries";

interface KpiParameterStatisticsProps {
	Id: number;
	StartDate: string | null;
	EndDate: string | null;
}

const normalizeQueryDate = (value: string | null): string | undefined => {
	if (!value) return undefined;
	const normalized = value.replaceAll(".", "-");
	const parsed = new Date(normalized);
	if (Number.isNaN(parsed.getTime())) return undefined;
	return normalized;
};

export const KpiParameterStatistics: FC<KpiParameterStatisticsProps> = ({
	Id,
	StartDate,
	EndDate,
}) => {
	const theme = useTheme();
	const navigate = useNavigate();

	const queryOptions = {
		StartDate: normalizeQueryDate(StartDate),
		EndDate: normalizeQueryDate(EndDate),
	};
	const { isLoading: isLoadingKpi, data: kpi } = useFetchTrainingKpi(Id);
	const { isLoading: isLoadingSessions, data: sessionsData } =
		useFetchDrillSessionsByKpiId(Id, queryOptions);

	const sessions = useMemo(
		() =>
			Array.isArray(sessionsData)
				? sessionsData.filter(
						(session): session is TrainingDrillSessionSummaryWithKpiValue =>
							session != null,
					)
				: [],
		[sessionsData],
	);
	const loading = isLoadingKpi || isLoadingSessions;
	const hasData = !!kpi && sessions.length > 0;

	const sorted = useMemo(
		() =>
			[...sessions].sort(
				(a, b) => new Date(a.Date).getTime() - new Date(b.Date).getTime(),
			),
		[sessions],
	);

	const values = useMemo(
		() => sorted.map((session) => Number(session.KpiValue ?? 0)),
		[sorted],
	);

	const stats = useMemo(() => {
		const count = values.length;
		const sum = values.reduce((acc, v) => acc + v, 0);
		const avg = count ? Math.round((sum / count) * 100) / 100 : 0;
		const min = count ? Math.min(...values) : 0;
		const max = count ? Math.max(...values) : 0;
		return { count, avg, min, max };
	}, [values]);

	const labels = useMemo(
		() => sorted.map((session) => dayjs(session.Date).format("DD/MM/YY")),
		[sorted],
	);
	const periodLabel = useMemo(() => {
		const start = queryOptions.StartDate ? dayjs(queryOptions.StartDate) : null;
		const end = queryOptions.EndDate ? dayjs(queryOptions.EndDate) : null;

		if (start?.isValid() && end?.isValid()) {
			return `${start.format("DD/MM/YY")} → ${end.format("DD/MM/YY")}`;
		}

		if (sorted.length > 0) {
			return `${dayjs(sorted[0].Date).format("DD/MM/YY")} → ${dayjs(
				sorted[sorted.length - 1].Date,
			).format("DD/MM/YY")}`;
		}

		return undefined;
	}, [queryOptions.EndDate, queryOptions.StartDate, sorted]);

	const primary = theme.palette.mode === "dark" ? "#7FB0FF" : "#4F8BFF";
	const reqColor = theme.palette.mode === "dark" ? "#FF8A72" : "#E0523D";
	const reqValue = Number(kpi?.MaxValue ?? 0);
	const tooltipMutedTextColor =
		theme.palette.mode === "dark"
			? "rgba(226,232,240,0.72)"
			: "rgba(71,85,105,0.9)";
	const tooltipShellSx = useMemo(
		() => ({
			renderMode: "html" as const,
			appendToBody: true,
			confine: false,
			enterable: true,
			transitionDuration: 0.08,
			backgroundColor:
				theme.palette.mode === "dark"
					? "rgba(15, 23, 42, 0.96)"
					: "rgba(255, 255, 255, 0.98)",
			borderColor: alpha(
				theme.palette.primary.main,
				theme.palette.mode === "dark" ? 0.45 : 0.22,
			),
			borderWidth: 1,
			borderRadius: 10,
			padding: [10, 12],
			textStyle: {
				color: theme.palette.text.primary,
				fontSize: 12,
			},
			extraCssText:
				"box-shadow: 0 10px 28px rgba(2,6,23,0.26); max-width: 360px; white-space: normal; z-index: 9999; backdrop-filter: blur(2px);",
		}),
		[theme],
	);
	const chartOption = useMemo<echarts.EChartsOption>(
		() => ({
			backgroundColor: "transparent",
			color: [primary, reqColor],
			grid: { left: 54, right: 14, top: 24, bottom: 42 },
			legend: { show: false },
			tooltip: {
				...tooltipShellSx,
				trigger: "axis",
				axisPointer: { type: "line" },
				formatter: (params: unknown) => {
					const list = (Array.isArray(params) ? params : [params]) as Array<{
						dataIndex?: number;
						seriesName?: string;
						data?: number;
						marker?: string;
					}>;
					const first = list[0];
					const idx = Number(first?.dataIndex ?? 0);
					const measured = values[idx] ?? 0;
					const status =
						measured > reqValue ? "Above requirement" : "Within requirement";

					return `
						<div style="max-width:330px">
							<div style="font-weight:700; margin-bottom:6px">${labels[idx] ?? ""}</div>
							${list
								.map(
									(item) => `
										<div style="display:flex; justify-content:space-between; gap:12px;">
											<span>${item.marker ?? ""}${item.seriesName ?? ""}</span>
											<strong>${Number(item.data ?? 0)}</strong>
										</div>
									`,
								)
								.join("")}
							<div style="margin-top:6px; color:${tooltipMutedTextColor}; font-size:12px;">
								${kpi?.Name ?? "KPI"}: ${status}
							</div>
						</div>
					`;
				},
			},
			xAxis: {
				type: "category",
				data: labels,
				boundaryGap: false,
				axisLine: { show: false },
				axisTick: { show: false },
				axisLabel: {
					color: theme.palette.text.secondary,
					fontSize: 11,
					margin: 12,
					rotate: labels.length > 10 ? -25 : 0,
				},
			},
			yAxis: {
				type: "value",
				minInterval: 1,
				name: kpi?.Name || "KPI",
				nameTextStyle: {
					color: theme.palette.text.primary,
					fontWeight: 600,
					padding: [0, 0, 4, 0],
				},
				splitLine: {
					show: true,
					lineStyle: { color: alpha(theme.palette.divider, 0.22) },
				},
				axisLine: { show: false },
				axisTick: { show: false },
				axisLabel: {
					color: theme.palette.text.secondary,
					fontSize: 11,
					margin: 12,
				},
			},
			series: [
				{
					name: kpi?.Name || "KPI",
					type: "line",
					data: values,
					smooth: true,
					symbol: "none",
					lineStyle: {
						width: 1.8,
						color: primary,
						cap: "round",
						join: "round",
					},
					itemStyle: {
						color: primary,
						borderWidth: 1.5,
						borderColor: theme.palette.background.paper,
					},
					emphasis: {
						scale: 1.08,
						lineStyle: { width: 2.1 },
					},
				},
				{
					name: "Requirement",
					type: "line",
					data: values.map(() => reqValue),
					symbol: "none",
					lineStyle: {
						width: 1.6,
						type: "dashed",
						color: reqColor,
					},
				},
			],
		}),
		[
			kpi?.Name,
			labels,
			primary,
			reqColor,
			reqValue,
			theme,
			tooltipMutedTextColor,
			tooltipShellSx,
			values,
		],
	);

	const valueChip = (val: number, maxVal: number) => {
		const color: "error" | "warning" | "success" =
			val > maxVal ? "error" : val === maxVal ? "warning" : "success";
		return (
			<Chip
				color={color}
				size="small"
				label={val}
				variant="outlined"
				sx={{ fontWeight: 700, height: 22, "& .MuiChip-label": { px: 0.75 } }}
			/>
		);
	};

	const Stat = ({
		label,
		value,
		accent = false,
	}: {
		label: string;
		value: number | string;
		accent?: boolean;
	}) => (
		<Chip
			size="small"
			label={
				<Stack direction="row" spacing={0.75} alignItems="center">
					<Typography
						component="span"
						variant="caption"
						sx={{ fontWeight: 700, color: "text.secondary" }}
					>
						{label}
					</Typography>
					<Typography component="span" variant="body2" sx={{ fontWeight: 800 }}>
						{value}
					</Typography>
				</Stack>
			}
			sx={{
				height: 30,
				borderRadius: 2,
				bgcolor: accent
					? alpha(theme.palette.warning.main, theme.palette.mode === "dark" ? 0.16 : 0.1)
					: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.14 : 0.06),
				border: `1px solid ${alpha(
					accent ? theme.palette.warning.main : theme.palette.primary.main,
					theme.palette.mode === "dark" ? 0.3 : 0.16,
				)}`,
				"& .MuiChip-label": { px: 1.1 },
			}}
		/>
	);

	const handleBack = () => {
		const params = new URLSearchParams();
		if (queryOptions.StartDate) params.set("StartDate", queryOptions.StartDate);
		if (queryOptions.EndDate) params.set("EndDate", queryOptions.EndDate);
		const qs = params.toString();
		navigate(`/training/archive${qs ? `?${qs}` : ""}`);
	};

	const handleRowClick = (drillId: number, sessionId: number) => {
		navigate(`/training/drills/${drillId}/sessions/${sessionId}`);
	};

	return (
		<Box
			sx={{
				flex: 1,
				minHeight: 0,
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}
		>
			{/* Compact header */}
			<Box
				sx={{
					flexShrink: 0,
					display: "flex",
					flexWrap: "wrap",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 1.5,
					py: 1.35,
					px: { xs: 2.5, sm: 3 },
					borderBottom: 1,
					borderColor: "divider",
				}}
			>
				<Tooltip
					title={<Translate String="Common.Back">Back</Translate>}
					enterDelay={400}
				>
					<IconButton
						onClick={handleBack}
						size="small"
						sx={{
							color: "primary.main",
							"&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08) },
						}}
					>
						<ArrowBackIosIcon sx={{ fontSize: 18 }} />
					</IconButton>
				</Tooltip>

				<Box sx={{ flex: 1, minWidth: 280 }}>
					<Typography
						variant="caption"
						sx={{
							display: "block",
							letterSpacing: "0.08em",
							fontWeight: 700,
							color: "primary.main",
							textTransform: "uppercase",
							mb: 0.15,
						}}
					>
						<Translate String="Auto.Features.Training.Pages.Archive.PanelRight.KpiParameterStatistics.L399C7.Analytics">Analytics</Translate>
					</Typography>
					<Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.1 }}>
						{loading ? <Skeleton width={180} /> : (kpi?.Name ?? "—")}
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 0.2 }}>
						{periodLabel ?? "KPI Statistics"}
					</Typography>
				</Box>

				{!loading && (
						<Stack
							direction="row"
							spacing={1}
							useFlexGap
							flexWrap="wrap"
							justifyContent="flex-end"
						>
							<Stat
								label={
									<Translate String="Common.Sessions">Sessions</Translate>
								}
								value={stats.count.toLocaleString()}
							/>
							<Stat
								label={
									<Translate String="Common.Average">Average</Translate>
								}
								value={stats.avg.toFixed(2)}
							/>
							<Stat
								label={<Translate String="Common.Min">Min</Translate>}
								value={stats.min}
							/>
							<Stat
								label={<Translate String="Common.Max">Max</Translate>}
								value={stats.max}
							/>
							<Stat
								label={
									<Translate String="Common.Requirement">Requirement</Translate>
								}
								value={kpi?.MaxValue ?? "—"}
								accent
							/>
						</Stack>
				)}
			</Box>

			{/* Scrollable content */}
			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					overflowY: "auto",
					overflowX: "hidden",
					scrollbarGutter: "stable both-edges",
					overscrollBehavior: "contain",
					px: { xs: 2.5, sm: 3 },
					py: { xs: 2, sm: 2.25 },
				}}
			>
				<Stack spacing={2.25}>
					<Box>
						<Paper
							variant="outlined"
							sx={{ borderRadius: 3, overflow: "hidden", borderColor: "divider" }}
						>
							<Box
								sx={(t) => ({
									px: 2.25,
									py: 1,
									bgcolor:
										t.palette.mode === "dark"
											? alpha(t.palette.common.white, 0.02)
											: "grey.50",
									borderBottom: 1,
									borderColor: alpha(t.palette.divider, 0.85),
								})}
							>
								<Typography
									variant="caption"
									sx={{
										display: "block",
										letterSpacing: "0.08em",
										fontWeight: 700,
										color: "primary.main",
										textTransform: "uppercase",
										mb: 0.1,
									}}
								>
									<Translate String="Auto.Features.Training.Pages.Archive.PanelRight.KpiParameterStatistics.L468C10.KPIChart">KPI chart</Translate>
								</Typography>
							</Box>

							<Box sx={{ p: 2 }}>
								<Stack
									direction={{ xs: "column", sm: "row" }}
									spacing={1}
									alignItems={{ xs: "flex-start", sm: "center" }}
									justifyContent="space-between"
									sx={{
										px: 1.25,
										py: 1,
										mb: 1.25,
										borderRadius: 2,
										bgcolor:
											theme.palette.mode === "dark"
												? alpha(theme.palette.common.white, 0.035)
												: alpha(theme.palette.primary.main, 0.03),
										border: `1px solid ${alpha(
											theme.palette.divider,
											theme.palette.mode === "dark" ? 0.34 : 0.72,
										)}`,
									}}
								>
									<Stack
										direction="row"
										spacing={1.25}
										useFlexGap
										flexWrap="wrap"
									>
										<Stack direction="row" spacing={0.75} alignItems="center">
											<Box
												sx={{
													width: 18,
													height: 3,
													borderRadius: 999,
													bgcolor: primary,
												}}
											/>
											<Typography variant="body2" fontWeight={700}>
												{kpi?.Name ?? "KPI"}
											</Typography>
										</Stack>
										<Stack direction="row" spacing={0.75} alignItems="center">
											<Box
												sx={{
													width: 18,
													height: 0,
													borderTop: `2px dashed ${reqColor}`,
												}}
											/>
											<Typography variant="body2" color="text.secondary" fontWeight={700}>
												<Translate String="Common.Requirement">
													Requirement
												</Translate>{" "}
												{kpi?.MaxValue ?? "—"}
											</Typography>
										</Stack>
									</Stack>

									<Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
										{stats.count > 0 ? (
											<>
												{stats.count}{" "}
												<Translate String="KpiParameterStatistics.DataPoints">
													data points
												</Translate>
											</>
										) : (
											<Translate String="KpiParameterStatistics.NoDataPointsInRange">
												No data points in range
											</Translate>
										)}
									</Typography>
								</Stack>

								<Box sx={{ height: 280, position: "relative" }}>
									{loading || !hasData ? (
										<Box
											height="100%"
											display="flex"
											alignItems="center"
											justifyContent="center"
										>
											{loading ? (
												<LinearProgress sx={{ width: "100%" }} />
											) : (
												<Typography color="text.secondary">
													<Translate String="Common.NoData">No data</Translate>
												</Typography>
											)}
										</Box>
									) : (
										<ReactECharts
											notMerge
											opts={{ renderer: "canvas" }}
											option={chartOption}
											style={{ height: "100%", width: "100%" }}
										/>
									)}
								</Box>
							</Box>
						</Paper>
					</Box>

					<Box>
						<Paper
							variant="outlined"
							sx={{ borderRadius: 3, overflow: "hidden", borderColor: "divider" }}
						>
							<Box
								sx={(t) => ({
									px: 2.25,
									py: 1,
									bgcolor:
										t.palette.mode === "dark"
											? alpha(t.palette.common.white, 0.02)
											: "grey.50",
									borderBottom: 1,
									borderColor: alpha(t.palette.divider, 0.85),
								})}
							>
								<Typography
									variant="caption"
									sx={{
										display: "block",
										letterSpacing: "0.08em",
										fontWeight: 700,
										color: "primary.main",
										textTransform: "uppercase",
										mb: 0.1,
									}}
								>
									<Translate String="Auto.Features.Training.Pages.Archive.PanelRight.KpiParameterStatistics.L586C10.DrillSessions">Drill sessions</Translate>
								</Typography>
							</Box>

							<Box sx={{ p: 2 }}>
								<Paper
									variant="outlined"
									sx={{
										borderRadius: 2,
										overflow: "hidden",
										borderColor: "divider",
									}}
								>
							{loading ? (
								<Box p={2} display="flex" gap={1}>
									<Skeleton
										variant="rectangular"
										height={28}
										sx={{ flex: 1 }}
									/>
								</Box>
							) : !hasData ? (
								<Box p={2}>
									<Typography color="text.secondary">
										<Translate String="KpiParameterStatistics.NoSessionsData">
											No sessions data available.
										</Translate>
									</Typography>
								</Box>
							) : (
								<TableContainer sx={{ maxHeight: 280 }}>
									<Table size="small" stickyHeader>
										<TableHead>
											<TableRow sx={{ "& th": { fontWeight: 700 } }}>
												<TableCell>
													<Translate String="Common.Name">Name</Translate>
												</TableCell>
												<TableCell align="center" width={100}>
													<Translate String="Common.Kpi">KPI</Translate>
												</TableCell>
												<TableCell align="center" width={100}>
													<Translate String="Common.RequirementShort">
														Req
													</Translate>
												</TableCell>
												<TableCell width={140}>
													<Translate String="Common.Date">Date</Translate>
												</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{sorted.map((session, idx) => (
												<TableRow
													key={session.Id}
													hover
													onClick={() =>
														handleRowClick(session.DrillId, session.Id)
													}
													sx={{
														cursor: "pointer",
														bgcolor:
															idx % 2 === 1
																? alpha(theme.palette.text.primary, 0.03)
																: "inherit",
													}}
												>
													<TableCell
														sx={{
															maxWidth: 280,
															whiteSpace: "nowrap",
															overflow: "hidden",
															textOverflow: "ellipsis",
															fontWeight: 600,
														}}
														title={session.Name}
													>
														{session.Name}
													</TableCell>
													<TableCell align="center">
														{valueChip(
															Number(session.KpiValue ?? 0),
															Number(kpi?.MaxValue ?? 0),
														)}
													</TableCell>
													<TableCell align="center">
														<Chip
															label={kpi?.MaxValue ?? "—"}
															size="small"
															variant="outlined"
															sx={{ height: 22, fontWeight: 700 }}
														/>
													</TableCell>
													<TableCell>
														{new Date(session.Date).toLocaleString(undefined, {
															day: "2-digit",
															month: "2-digit",
															year: "2-digit",
															hour: "2-digit",
															minute: "2-digit",
															hour12: false,
														})}
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</TableContainer>
							)}
								</Paper>
							</Box>
						</Paper>
					</Box>
				</Stack>
			</Box>
		</Box>
	);
};
